'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useState, createRef, useEffect, useMemo } from 'react';

import { useSearchParams } from 'next/navigation';

import { SearchViewModel } from '@api/v3';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { useSearchURL } from '@api/data-loaders/resource';
import {
  convertDateToMomentObject,
  DateType,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import { paramsToObject } from '@components/views/utils';
import { generateUUID } from '@utils/generateUUID';

import { createGenericContext } from '../generateContext';

type ContextInterface = {
  location: SearchViewModel;
  setLocation: React.Dispatch<React.SetStateAction<SearchViewModel>>;
  activeLocation: SearchViewModel;
  setActiveLocation: React.Dispatch<React.SetStateAction<SearchViewModel>>;
  startDate: DateType | null;
  endDate: DateType | null;
  setDates: React.Dispatch<
    React.SetStateAction<{
      startDate: DateType | null;
      endDate: DateType | null;
    }>
  >;
  hotelRoomsList: Array<ListingSearchRoomInterface>;
  setHotelRoomsList: React.Dispatch<
    React.SetStateAction<ListingSearchRoomInterface[]>
  >;
  isHotelDateSelected: boolean;
  setHotelDateSelection: React.Dispatch<React.SetStateAction<boolean>>;

  isFetchedItems: boolean;
  setIsFetchedItems: React.Dispatch<React.SetStateAction<boolean>>;
  isStickySearch: boolean;
  setIsStickySearch: React.Dispatch<React.SetStateAction<boolean>>;

  isLocationEntered: boolean;
  setIsLocationEntered: React.Dispatch<React.SetStateAction<boolean>>;
};

const [useSearchBarContext, SearchBarContextProvider] =
  createGenericContext<ContextInterface>();

const useSearchBarUtils = (
  initialLocation = {
    id: 0,
    text: '',
    url: '',
  }
) => {
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const [hotelRoomsList, setHotelRoomsList] = useState<
    Array<ListingSearchRoomInterface>
  >([
    {
      title: '1. Oda',
      id: generateUUID(),
      adultCount: 2,
      childrenList: [],
      closable: true,
      error: false,
      key: '1',
      ref: createRef<HTMLDivElement>(),
    },
  ]);

  const [location, setLocation] = useState<SearchViewModel>(initialLocation);

  const [activeLocation, setActiveLocation] =
    useState<SearchViewModel>(initialLocation);

  const [{ startDate, endDate }, setDates] = useState<{
    startDate: DateType | null;
    endDate: DateType | null;
  }>({ startDate: null, endDate: null });
  const [isFetchedItems, setIsFetchedItems] = useState<boolean>(false);
  const [isHotelDateSelected, setHotelDateSelection] = useState<boolean>(false);

  const [isStickySearch, setIsStickySearch] = useState<boolean>(false);

  const [isLocationEntered, setIsLocationEntered] = useState<boolean>(true);

  useEffect(() => {
    const newStartDate = query.in
      ? convertDateToMomentObject(query.in as string, HOTEL_SEARCH_DATE_FORMAT)
      : null;
    const newEndDate = query.out
      ? convertDateToMomentObject(query.out as string, HOTEL_SEARCH_DATE_FORMAT)
      : null;

    setDates((prevDates) => {
      if (
        prevDates.startDate !== newStartDate ||
        prevDates.endDate !== newEndDate
      ) {
        return { startDate: newStartDate, endDate: newEndDate };
      } else return prevDates;
    });
  }, [query]);

  return {
    hotelRoomsList,
    setHotelRoomsList,
    location,
    setLocation,
    startDate,
    endDate,
    setDates,
    isHotelDateSelected,
    setHotelDateSelection,
    activeLocation,
    setActiveLocation,
    isFetchedItems,
    setIsFetchedItems,
    isStickySearch,
    setIsStickySearch,
    isLocationEntered,
    setIsLocationEntered,
  };
};

const SearchBarProvider = (props: React.PropsWithChildren<unknown>) => {
  const value = useSearchBarUtils();

  return (
    <SearchBarContextProvider value={value}>
      {props.children}
    </SearchBarContextProvider>
  );
};

const SSrSearchBarProvider = (props: React.PropsWithChildren<unknown>) => {
  const initialLocation = useSearchURL();

  const value = useSearchBarUtils({ ...initialLocation, url: '' });

  return (
    <SearchBarContextProvider value={value}>
      {props.children}
    </SearchBarContextProvider>
  );
};

export { useSearchBarContext, SearchBarProvider, SSrSearchBarProvider };
